import { useForm, zodResolver } from "@mantine/form";
import { useGrantWhiteboardPermission } from "@src/components/board/mutations/grant-whiteboard-permission";
import { whiteboardQueryKeys } from "@src/components/board/queries/query-keys";
import { WhiteboardDocumentId } from "@src/components/collaboration/document-id";
import { FeatureGate } from "@src/components/plan/feature-gate/feature-gate";
import {
	FeatureEntity,
	SupportedFeature,
	type WhiteboardPermission,
} from "@src/gql/graphql";
import { queryClient } from "@src/query-client";
import { captureBoardShared } from "@src/utils/analytics";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import type { z } from "zod";
import { useRevokeWhiteboardPermission } from "../board/mutations/revoke-whiteboard-permission";
import { useTransferWhiteboard } from "../board/mutations/transfer-whiteboard";
import { fetchActiveWhiteboardQueryOptions } from "../board/queries/fetch-active-whiteboard";
import { getPublishedLink } from "../board/use-whiteboard-navigation";
import { useAuthenticatedUser } from "../user/user-context";
import { useSyncCollaboratorsUpdateToForm } from "./hooks/use-sync-collaborators";
import { InviteFormLayout } from "./invite-form-layout";
import {
	detectUserChanges,
	executePermissionUpdates,
	generatePermissionUpdates,
} from "./permission-updates";
import { QuotaNoticeSection } from "./quota-notice-section";
import {
	checkQuotaExceeded,
	organizeUsers,
	processEmailInput,
	processNewUsers,
} from "./user-processing";
import { getUserFromCollaborator } from "./whiteboard-invite-fns";
import { inviteFormSchema } from "./whiteboard-invite-schema";

export function ShareWhiteboardInviteForm(props: {
	id: string;
	permissions: {
		enabled: boolean;
		stats?: {
			available: boolean;
			current: number;
			quota: number;
		};
	};
}) {
	const authenticatedUser = useAuthenticatedUser();
	const [isCopyLinkTooltipOpen, setIsCopyLinkTooltipOpen] = useState(false);

	const { data: whiteboardData } = useQuery(
		fetchActiveWhiteboardQueryOptions(new WhiteboardDocumentId(props.id), null),
	);

	const { mutateAsync: grantPermissionsToWhiteboard } =
		useGrantWhiteboardPermission();
	const { mutateAsync: transferWhiteboard } = useTransferWhiteboard();
	const { mutateAsync: revokePermissionToWhiteboard } =
		useRevokeWhiteboardPermission();

	const collaborators = useMemo(
		() => whiteboardData?.collaboration?.collaborators ?? [],
		[whiteboardData],
	);

	const boardDocumentId = useMemo(
		() => new WhiteboardDocumentId(props.id),
		[props.id],
	);

	const form = useForm<z.infer<typeof inviteFormSchema>>({
		mode: "controlled",
		initialValues: {
			selectedRole: props.permissions.enabled ? "Editor" : "Viewer",
			emails: "",
			users: collaborators.map(getUserFromCollaborator),
		},
		validate: zodResolver(inviteFormSchema),
	});

	useSyncCollaboratorsUpdateToForm(form, collaborators);

	const handleFlashCopyLinkTooltip = () => {
		setIsCopyLinkTooltipOpen(true);
		setTimeout(() => {
			setIsCopyLinkTooltipOpen(false);
		}, 1000);
	};

	const handleCopyLinkToClipboard = () => {
		navigator.clipboard.writeText(getPublishedLink(props.id, null));
		handleFlashCopyLinkTooltip();
	};

	const grantPermissionFn = useCallback(
		async ({
			entityId,
			userEmails,
			permission,
		}: {
			entityId: string;
			userEmails: string[];
			permission: WhiteboardPermission;
		}) =>
			grantPermissionsToWhiteboard({
				whiteboardId: entityId,
				userEmails,
				permission,
			}),
		[grantPermissionsToWhiteboard],
	);

	const revokePermissionFn = useCallback(
		async ({ entityId, userEmail }: { entityId: string; userEmail: string }) =>
			revokePermissionToWhiteboard({
				whiteboardId: entityId,
				userEmail,
			}),
		[revokePermissionToWhiteboard],
	);

	const transferFn = useCallback(
		async ({
			entityId,
			newOwnerEmail,
		}: { entityId: string; newOwnerEmail: string }) =>
			transferWhiteboard({
				whiteboardId: entityId,
				newOwnerEmail,
			}),
		[transferWhiteboard],
	);

	const handleSubmit = form.onSubmit(async (values) => {
		const newUsers = processEmailInput(values.emails, values.selectedRole);
		const quota = props.permissions.stats?.quota;

		const { sortedUsers, quotaExceeded } = organizeUsers(
			values.users,
			newUsers,
			authenticatedUser.email,
			quota,
		);

		// Editor-seat quota only applies when inviting editors.
		if (
			values.selectedRole === "Editor" &&
			checkQuotaExceeded(quotaExceeded, quota ?? 0, form)
		) {
			return;
		}

		form.setFieldValue("users", sortedUsers);

		const inviteSuccess = await processNewUsers(
			newUsers,
			props.id,
			values.selectedRole,
			grantPermissionFn,
			form,
			handleCopyLinkToClipboard,
			"whiteboard",
		);
		if (inviteSuccess && newUsers.length > 0) {
			captureBoardShared(boardDocumentId, newUsers.length);
		}

		const { changedUsers, removedUsers } = detectUserChanges(
			form.values.users,
			values.users,
		);

		const promises = generatePermissionUpdates(
			changedUsers,
			removedUsers,
			props.id,
			grantPermissionFn,
			revokePermissionFn,
			transferFn,
		);

		await executePermissionUpdates(promises);

		queryClient.invalidateQueries({
			queryKey: whiteboardQueryKeys.ofId(props.id),
		});
	});

	const entityName = whiteboardData?.name ?? "Whiteboard";

	return (
		<FeatureGate
			feature={SupportedFeature.CreateBoards}
			entity={{
				id: props.id,
				type: FeatureEntity.Whiteboard,
			}}
			render={({ enabled, stats }) => (
				<InviteFormLayout
					form={form}
					handleSubmit={handleSubmit}
					entityName={entityName}
					copyLinkLabel="Board Link"
					isCopyLinkTooltipOpen={isCopyLinkTooltipOpen}
					onCopyLink={handleCopyLinkToClipboard}
					// Keep invites enabled so viewers can still be added when
					// editor seats are exhausted; stats gate Editor only.
					emailControlsEnabled
					emailControlsStats={props.permissions.stats}
					disableEditorRoles={
						!enabled ||
						!props.permissions.enabled ||
						stats?.available === false ||
						props.permissions.stats?.available === false
					}
					footerNotice={<QuotaNoticeSection whiteboardId={props.id} />}
				/>
			)}
		/>
	);
}
